import { useEffect, useRef } from 'react';

import NumberFlow from '@number-flow/react';
import classnames from 'classnames';

import { useCountDownSeconds } from '../../../hooks/useCountDownTimer';
import { BaseComponentProps } from '../../../types/base-component.types';

import styles from './Countdown.module.scss';

export type ContainerProps = BaseComponentProps & {
  seconds: number;
  onTimeout: () => void;
  /** Drawn over the clock, for a count that belongs to a numbered question. */
  index?: number;
  /**
   * What the clock reads once it runs out. Left unset it holds at zero, which
   * is what a count that closes its own screen wants; a count that waits on
   * something afterwards says so instead.
   */
  waitingLabel?: string;
  /** `lg` is the clock a screen is built around, `md` one set inside a page. */
  size?: 'md' | 'lg';
};

function Countdown({
  className,
  seconds,
  onTimeout,
  index,
  waitingLabel,
  size = 'md',
  testId = 'ui-challenge-countdown',
}: ContainerProps) {
  const secondsLeft = useCountDownSeconds(seconds);

  // The caller hands us a new closure on every render. Kept in a ref, the clock
  // below is the only thing the timeout waits on, so a parent that re-renders
  // mid-count cannot restart it.
  const onTimeoutRef = useRef(onTimeout);

  // Zero is reached once. React double-invokes effects on mount under
  // StrictMode, and a `seconds={0}` clock would otherwise report it twice — for
  // the challenge that is the same question asked of the server twice.
  const reported = useRef(false);

  useEffect(() => {
    onTimeoutRef.current = onTimeout;
  }, [onTimeout]);

  useEffect(() => {
    if (secondsLeft === 0 && !reported.current) {
      reported.current = true;
      onTimeoutRef.current();
    }
  }, [secondsLeft]);

  return (
    <div
      // The count is the reader's to look at, not to be read out: a value that
      // changes every second would talk over everything else on the screen.
      role="timer"
      aria-live="off"
      className={classnames(styles['main'], styles[`main--${size}`], className)}
      data-test-id={testId}
    >
      {index !== undefined && (
        <span className={styles['index']}>Pregunta {index}</span>
      )}
      <span className={classnames(styles['timer'], styles[`timer--${size}`])}>
        {secondsLeft === 0 && waitingLabel !== undefined ? (
          waitingLabel
        ) : (
          // Always rolled downward. The clock only ever descends, so reading the
          // direction off each step is work done to reach the same answer.
          <NumberFlow value={secondsLeft} locales={['es']} trend={-1} />
        )}
      </span>
    </div>
  );
}

export { Countdown };
